import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useQuery, useMutation } from '@tanstack/react-query';
import { formsApi, type FormDto } from '../../api/forms';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface DynamicFormProps {
    formId: string;
    relatedEntityId?: string;
    relatedEntityType?: string;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({ formId, relatedEntityId, relatedEntityType }) => {
    const { t } = useTranslation();
    const { siteTokens } = useThemeSettings();
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const recaptchaSiteKey = siteTokens.RecaptchaSiteKey;

    const { data: formDefinition, isLoading, isError } = useQuery({
        queryKey: ['publicForm', formId],
        queryFn: () => formsApi.getPublicForm(formId),
        select: (res) => res.data as FormDto
    });

    const submitMutation = useMutation({
        mutationFn: formsApi.submitForm,
        onSuccess: () => {
            setIsSubmitted(true);
            setFormData({});
        },
        onError: (error: any) => {
            alert(error.response?.data?.detail || 'Submission failed. Please try again.');
            recaptchaRef.current?.reset();
        }
    });

    const handleInputChange = (name: string, value: any) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            const newErrors = { ...errors };
            delete newErrors[name];
            setErrors(newErrors);
        }
    };

    const validate = () => {
        const newErrors: Record<string, string> = {};
        formDefinition?.fields.forEach(field => {
            if (field.isRequired && !formData[field.name]) {
                newErrors[field.name] = t('common.field_required') || 'This field is required';
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validate()) return;

        const token = await recaptchaRef.current?.executeAsync();
        if (!token) {
            alert('Please complete the Recaptcha validation.');
            return;
        }

        submitMutation.mutate({
            formDefinitionId: formId,
            data: formData,
            recaptchaToken: token,
            relatedEntityId,
            relatedEntityType
        });
    };

    if (isLoading) return <div className="animate-pulse flex space-y-4 flex-col p-6"><div className="h-4 bg-gray-200 rounded w-3/4"></div><div className="h-10 bg-gray-100 rounded"></div></div>;
    if (isError || !formDefinition) return null;

    if (isSubmitted) {
        return (
            <div className="bg-green-50 border border-green-100 p-8 rounded-3xl text-center">
                <h3 className="text-xl font-bold text-green-800 mb-2">
                    {formDefinition.successMessage || t('common.success') || 'Success!'}
                </h3>
                <p className="text-green-700">
                    {t('forms.submission_received') || 'Your submission has been received.'}
                </p>
                <button 
                    onClick={() => setIsSubmitted(false)}
                    className="mt-6 text-sm font-semibold text-green-800 underline hover:no-underline"
                >
                    {t('forms.submit_another') || 'Submit another response'}
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="bg-foreground/[0.02] border border-border p-8 rounded-3xl">
            <h3 className="text-xl font-bold mb-2">{formDefinition.title}</h3>
            {formDefinition.description && <p className="text-foreground/60 mb-8">{formDefinition.description}</p>}

            <div className="space-y-6">
                {formDefinition.fields.map(field => (
                    <div key={field.id} className="flex flex-col gap-2">
                        <label className="text-sm font-bold text-foreground/70">
                            {field.label} {field.isRequired && <span className="text-red-500">*</span>}
                        </label>

                        {field.type === 'textarea' ? (
                            <textarea
                                className={`w-full px-4 py-3 rounded-xl border ${errors[field.name] ? 'border-red-500' : 'border-border'} bg-white focus:ring-2 focus:ring-primary/20 outline-none transition-all min-h-[120px]`}
                                placeholder={field.placeholder}
                                value={formData[field.name] || ''}
                                onChange={e => handleInputChange(field.name, e.target.value)}
                            />
                        ) : field.type === 'select' ? (
                            <select
                                className={`w-full px-4 py-3 rounded-xl border ${errors[field.name] ? 'border-red-500' : 'border-border'} bg-white focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
                                value={formData[field.name] || ''}
                                onChange={e => handleInputChange(field.name, e.target.value)}
                            >
                                <option value="">{field.placeholder || t('common.select_option') || 'Select an option'}</option>
                                {field.options?.split(',').map(opt => (
                                    <option key={opt.trim()} value={opt.trim()}>{opt.trim()}</option>
                                ))}
                            </select>
                        ) : field.type === 'checkbox' ? (
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                                    checked={!!formData[field.name]}
                                    onChange={e => handleInputChange(field.name, e.target.checked)}
                                />
                                <span className="text-sm text-foreground/60">{field.helpText}</span>
                            </div>
                        ) : (
                            <input
                                type={field.type}
                                className={`w-full px-4 py-3 rounded-xl border ${errors[field.name] ? 'border-red-500' : 'border-border'} bg-white focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
                                placeholder={field.placeholder}
                                value={formData[field.name] || ''}
                                onChange={e => handleInputChange(field.name, e.target.value)}
                            />
                        )}

                        {errors[field.name] && <span className="text-xs text-red-500">{errors[field.name]}</span>}
                        {!errors[field.name] && field.helpText && <span className="text-xs text-foreground/40">{field.helpText}</span>}
                    </div>
                ))}

                {recaptchaSiteKey && (
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={recaptchaSiteKey}
                    />
                )}

                <button
                    type="submit"
                    disabled={submitMutation.isPending}
                    className="w-full bg-primary text-white font-bold py-4 rounded-2xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50 mt-4"
                >
                    {submitMutation.isPending ? '...' : (formDefinition.submitButtonText || t('common.submit') || 'Submit')}
                </button>
            </div>
        </form>
    );
};
